import { APP_CONSTANTS } from '../config/constants';
import { Platform } from 'react-native';
import { initCircularLogBuffer } from './logBuffer';
import { routineLogSummary } from './routineLogSummary';
import {
  addSentryBreadcrumb,
  captureSentryLoggerError,
  initializeSentry,
} from '../services/monitoring/sentry';

// Inicializa o buffer circular de logs (se habilitado)
initCircularLogBuffer();
initializeSentry();

/**
 * Tipos de logs disponíveis
 */
export enum LogLevel {
  ERROR = 1,
  WARN = 2,
  INFO = 3,
  DEBUG = 4,
  VERBOSE = 5,
}

/**
 * Interface para um logger
 */
export interface ILogger {
  error(message: string, ...args: any[]): void;
  warn(message: string, ...args: any[]): void;
  info(message: string, ...args: any[]): void;
  debug(message: string, ...args: any[]): void;
  verbose(message: string, ...args: any[]): void;
}

// Interface para informações seguras de sessão
interface SafeSessionInfo {
  valid: boolean;
  user_id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string;
  expiresAt: string;
  expiresIn: string;
  storage_type: string;
  provider: string;
  access_token?: any;
  refresh_token?: any;
  reason?: string;
  error?: any;
  hasAccessToken?: boolean;
  hasRefreshToken?: boolean;
}

// Interface para informações seguras de token
interface SafeTokenInfo {
  valid: boolean;
  exp?: string;
  iat?: string;
  session_id?: string;
  role?: string;
  expiresIn?: string;
  tokenStart?: string;
  tokenEnd?: string;
  reason?: string;
  error?: any;
  tokenLength?: number;
  tokenStartChars?: string;
  tokenPart?: string;
}

/**
 * Verifica se o debug está habilitado para um módulo específico
 */
const isDebugEnabled = (module: string, subModule?: string): boolean => {
  // Se debug global estiver desabilitado, retorna false
  if (!APP_CONSTANTS.DEBUG.ENABLED) {
    return false;
  }

  // Verifica se o módulo existe nas configurações
  const moduleConfig = APP_CONSTANTS.DEBUG.MODULES[module as keyof typeof APP_CONSTANTS.DEBUG.MODULES];
  if (!moduleConfig) {
    return false;
  }

  // Verifica se o módulo está habilitado
  if (!moduleConfig.ENABLED) {
    return false;
  }

  // Se não houver submódulo, retorna true
  if (!subModule) {
    return true;
  }

  // Verifica se o submódulo está habilitado
  return !!moduleConfig[subModule as keyof typeof moduleConfig];
};

/**
 * Verifica se o nível de log é permitido
 */
const isLogLevelEnabled = (level: LogLevel): boolean => {
  return level <= APP_CONSTANTS.DEBUG.LOG_LEVEL;
};

// Contador global incremental para todos os logs. Ajuda a rastrear ordem exata.
let __GLOBAL_LOG_COUNTER = 0;

// Formata Date no padrão dd/MM/yy -- HH:mm:ss:SSS
const formatFullTimestamp = (date: Date): string => {
  const pad = (n: number, size: number = 2) => n.toString().padStart(size, '0');
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = pad(date.getFullYear() % 100); // 2 dígitos
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const millis = pad(date.getMilliseconds(), 3);
  return `${day}/${month}/${year} -- ${hours}:${minutes}:${seconds}:${millis}`;
};

const formatMessage = (module: string, subModule: string | undefined, message: string): string => {
  // Incrementa contador global e prepara string com zero à esquerda (6 dígitos)
  __GLOBAL_LOG_COUNTER += 1;
  const counterStr = __GLOBAL_LOG_COUNTER.toString().padStart(6, '0');

  // Timestamp completo no formato solicitado
  const timestamp = formatFullTimestamp(new Date());

  // Prefixo de módulo
  const prefix = subModule ? `[${module}:${subModule}]` : `[${module}]`;

  // Exemplo: [000123 | 23/06/24 -- 15:12:08:123] [MODULO:SUB] mensagem
  return `[${counterStr} | ${timestamp}] ${prefix} ${message}`;
};

/**
 * Funções auxiliares para debug de tokens e sessões
 */
export const TokenUtils = {
  /**
   * Decodifica uma string base64 de forma compatível com mobile/web
   * Esta função substitui o uso de Buffer.from para compatibilidade cross-platform
   */
  base64Decode: (str: string): string => {
    try {
      // No ambiente web, o atob está disponível
      if (typeof atob === 'function') {
        return atob(str.replace(/-/g, '+').replace(/_/g, '/'));
      }

      // No React Native/ambiente móvel, precisamos implementar nossa própria decodificação
      // Implementação básica para React Native
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
      let output = '';
      str = String(str).replace(/=+$/, '');

      if (str.length % 4 === 1) {
        throw new Error('Invalid base64 string');
      }

      for (
        let bc = 0, bs = 0, buffer, i = 0;
        (buffer = str.charAt(i++));
        ~buffer && (bs = bc % 4 ? bs * 64 + buffer : buffer, bc++ % 4)
          ? (output += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6))))
          : 0
      ) {
        buffer = chars.indexOf(buffer);
      }

      return output;
    } catch (error) {
      console.error('Erro na decodificação base64:', error);
      return '';
    }
  },

  /**
   * Extrai e retorna informações seguras de um token JWT
   * Não exibe o token completo, apenas partes relevantes e seguras
   */
  getSafeTokenInfo: (token: string | undefined | null): any => {
    if (!token) return { valid: false, reason: 'Token não fornecido' };

    try {
      // Não mostrar o token completo, apenas partes dele
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return { valid: false, reason: 'Formato de token inválido' };

      // Decodificar a parte de payload do token (segunda parte) usando método compatível com mobile
      let payload;
      try {
        payload = JSON.parse(TokenUtils.base64Decode(tokenParts[1]));
      } catch (e) {
        return {
          valid: false,
          reason: 'Erro ao decodificar token',
          error: e,
          tokenPart: tokenParts[1].substring(0, 10) + '...'
        };
      }

      // Extrair informações relevantes, mas seguras
      return {
        valid: true,
        exp: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'N/A',
        iat: payload.iat ? new Date(payload.iat * 1000).toISOString() : 'N/A',
        session_id: payload.session_id || 'N/A',
        role: payload.role || 'N/A',
        expiresIn: payload.exp ? Math.floor((payload.exp * 1000 - Date.now()) / 1000) + 's' : 'N/A',
        // Adicionar partes iniciais e finais do token para debugging (não o token completo)
        tokenStart: token.substring(0, 10) + '...',
        tokenEnd: '...' + token.substring(token.length - 10),
      };
    } catch (error) {
      return {
        valid: false,
        reason: 'Erro ao analisar token',
        error,
        // Incluir informações para debug
        tokenLength: token ? token.length : 0,
        tokenStartChars: token ? token.substring(0, 10) + '...' : 'N/A'
      };
    }
  },

  /**
   * Extrai informações seguras de uma sessão
   * Não exibe tokens completos, apenas informações relevantes para debug
   */
  getSafeSessionInfo: (session: any): SafeSessionInfo => {
    if (!session) return {
      valid: false,
      reason: 'Sessão não fornecida',
      user_id: 'N/A',
      email: 'N/A',
      created_at: 'N/A',
      last_sign_in_at: 'N/A',
      expiresAt: 'N/A',
      expiresIn: 'N/A',
      storage_type: 'N/A',
      provider: 'N/A'
    };

    try {
      // Extrair apenas os dados relevantes e seguros da sessão
      const result: SafeSessionInfo = {
        valid: true,
        user_id: session.user?.id || 'N/A',
        email: session.user?.email || 'N/A',
        created_at: session.created_at || 'N/A',
        last_sign_in_at: session.user?.last_sign_in_at || 'N/A',
        expiresAt: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'N/A',
        expiresIn: session.expires_at ? Math.floor((session.expires_at * 1000 - Date.now()) / 1000) + 's' : 'N/A',
        // Informações de storage
        storage_type: session.storage_type || 'N/A',
        provider: session.user?.app_metadata?.provider || 'N/A',
      };

      // Extrair informações seguras do token de acesso, se disponível
      if (session.access_token) {
        try {
          result.access_token = TokenUtils.getSafeTokenInfo(session.access_token);
        } catch (e) {
          result.access_token = {
            valid: false,
            reason: 'Erro ao analisar access_token',
            error: e,
            tokenLength: session.access_token.length
          };
        }
      } else {
        result.access_token = null;
      }

      // Informações simplificadas do refresh token
      if (session.refresh_token) {
        try {
          // Para refresh tokens, que podem não ser JWTs válidos, apenas armazenamos metadados
          result.refresh_token = {
            present: true,
            length: session.refresh_token.length,
            start: session.refresh_token.substring(0, 5) + '...',
            end: '...' + session.refresh_token.substring(session.refresh_token.length - 5)
          };
        } catch (e) {
          result.refresh_token = {
            present: true,
            error: 'Erro ao obter metadados do refresh token'
          };
        }
      } else {
        result.refresh_token = null;
      }

      return result;
    } catch (error) {
      return {
        valid: false,
        reason: 'Erro ao analisar sessão',
        error,
        // Tenta extrair algumas informações básicas mesmo em caso de erro
        user_id: session?.user?.id || 'N/A',
        email: 'N/A',
        created_at: 'N/A',
        last_sign_in_at: 'N/A',
        expiresAt: 'N/A',
        expiresIn: 'N/A',
        storage_type: 'N/A',
        provider: 'N/A',
        hasAccessToken: !!session?.access_token,
        hasRefreshToken: !!session?.refresh_token
      };
    }
  }
};

/**
 * Cria um logger para um módulo específico
 */
export const createLogger = (module: string, subModule?: string): ILogger => {
  const enabled = isDebugEnabled(module, subModule);
  const category = subModule ? `${module}:${subModule}` : module;
  const summarizeRoutine = (level: 'info' | 'debug', message: string, args: any[]): boolean => (
    process.env.EXPO_PUBLIC_DETAILED_LOGS !== 'true' && args.length === 0 &&
    routineLogSummary.suppress(category, message, summary => {
      const summaryMessage = formatMessage(module, subModule, 'Resumo de logs rotineiros');
      console[level](summaryMessage, summary);
      addSentryBreadcrumb({ category, message: summaryMessage, level, data: summary });
    })
  );

  return {
    error: (message: string, ...args: any[]) => {
      if (enabled && isLogLevelEnabled(LogLevel.ERROR)) {
        const formattedMessage = formatMessage(module, subModule, message);
        console.error(formattedMessage, ...args);
        addSentryBreadcrumb({
          category,
          message: formattedMessage,
          level: 'error',
          args,
        });
        captureSentryLoggerError({
          module,
          subModule,
          message: formattedMessage,
          args,
        });
      }
    },
    warn: (message: string, ...args: any[]) => {
      if (enabled && isLogLevelEnabled(LogLevel.WARN)) {
        const formattedMessage = formatMessage(module, subModule, message);
        console.warn(formattedMessage, ...args);
        addSentryBreadcrumb({
          category,
          message: formattedMessage,
          level: 'warning',
          args,
        });
      }
    },
    info: (message: string, ...args: any[]) => {
      if (enabled && isLogLevelEnabled(LogLevel.INFO)) {
        if (summarizeRoutine('info', message, args)) return;
        const formattedMessage = formatMessage(module, subModule, message);
        console.info(formattedMessage, ...args);
        addSentryBreadcrumb({
          category,
          message: formattedMessage,
          level: 'info',
          args,
        });
      }
    },
    debug: (message: string, ...args: any[]) => {
      if (enabled && isLogLevelEnabled(LogLevel.DEBUG)) {
        if (summarizeRoutine('debug', message, args)) return;
        const formattedMessage = formatMessage(module, subModule, message);
        console.debug(formattedMessage, ...args);
        addSentryBreadcrumb({
          category,
          message: formattedMessage,
          level: 'debug',
          args,
        });
      }
    },
    verbose: (message: string, ...args: any[]) => {
      if (enabled && isLogLevelEnabled(LogLevel.VERBOSE)) {
        const formattedMessage = formatMessage(module, subModule, message);
        console.log(formattedMessage, ...args);
        addSentryBreadcrumb({
          category,
          message: formattedMessage,
          level: 'debug',
          args,
        });
      }
    }
  };
};

export const SupabaseLogger = {
  Login: createLogger('SUPABASE', 'LOGIN'),
  Signup: createLogger('SUPABASE', 'SIGNUP'),
  ForgotPassword: createLogger('SUPABASE', 'FORGOT_PASSWORD'),
  Session: createLogger('SUPABASE', 'SESSION'),
  Main: createLogger('SUPABASE'),

  // Métodos auxiliares específicos para tokens e sessões
  logSessionState: (session: any, context: string = 'Estado da sessão'): void => {
    const sessionLogger = createLogger('SUPABASE', 'SESSION');
    if (!session) {
      sessionLogger.debug(`${context}: Sessão não encontrada ou nula`);
      return;
    }

    const safeInfo = TokenUtils.getSafeSessionInfo(session);
    sessionLogger.debug(`${context}:`, safeInfo);

    // Adicionar log específico sobre tempo de expiração para alertas
    if (safeInfo.valid && safeInfo.expiresIn) {
      const expiresInSeconds = parseInt(safeInfo.expiresIn);
      if (!isNaN(expiresInSeconds)) {
        const expiresInMinutes = Math.floor(expiresInSeconds / 60);

        if (expiresInMinutes < 5) {
          sessionLogger.warn(`${context}: ALERTA - Sessão expira em menos de 5 minutos (${expiresInMinutes}m ${expiresInSeconds % 60}s)`);
        } else if (expiresInMinutes < 30) {
          sessionLogger.info(`${context}: Sessão expira em ${expiresInMinutes} minutos`);
        }
      }
    }
  },

  logTokenState: (token: string | null | undefined, tokenType: string = 'access', context: string = 'Estado do token'): void => {
    const sessionLogger = createLogger('SUPABASE', 'SESSION');
    if (!token) {
      sessionLogger.debug(`${context}: Token ${tokenType} não encontrado ou nulo`);
      return;
    }

    const tokenInfo = TokenUtils.getSafeTokenInfo(token);
    sessionLogger.debug(`${context} (${tokenType}):`, tokenInfo);

    // Adicionar log específico sobre expiração para alertas
    if (tokenInfo.valid && tokenInfo.expiresIn) {
      const expiresInSeconds = parseInt(tokenInfo.expiresIn);
      if (!isNaN(expiresInSeconds)) {
        if (expiresInSeconds < 300) { // menos de 5 minutos
          sessionLogger.warn(`${context}: ALERTA - Token ${tokenType} expira em menos de 5 minutos (${expiresInSeconds}s)`);
        }
      }
    }
  },

  logStorageState: (storageKey: string, value: any, context: string = 'Estado do storage'): void => {
    const sessionLogger = createLogger('SUPABASE', 'SESSION');

    // Log básico do estado de armazenamento
    sessionLogger.debug(`${context} [${storageKey}]:`, typeof value === 'string' ?
      (value ? `Presente (${value.length} caracteres)` : 'Ausente/vazio') :
      value ? 'Presente (não-string)' : 'Ausente/vazio');

    // Tentar decodificar JSON para inspeção
    if (typeof value === 'string' && value) {
      try {
        const parsed = JSON.parse(value);

        // Log seguro para sessões armazenadas
        if (parsed.access_token || parsed.refresh_token) {
          sessionLogger.debug(`${context} [${storageKey}] conteúdo:`, {
            has_access_token: !!parsed.access_token,
            has_refresh_token: !!parsed.refresh_token,
            expires_at: parsed.expires_at ? new Date(parsed.expires_at * 1000).toISOString() : 'N/A',
            user_id: parsed.user?.id || 'N/A'
          });

          // Verificar expiração
          if (parsed.expires_at) {
            const expiresInSeconds = Math.floor((parsed.expires_at * 1000 - Date.now()) / 1000);
            if (expiresInSeconds < 0) {
              sessionLogger.warn(`${context} [${storageKey}]: Sessão armazenada EXPIRADA (${-expiresInSeconds}s atrás)`);
            } else if (expiresInSeconds < 300) {
              sessionLogger.warn(`${context} [${storageKey}]: Sessão armazenada expira em menos de 5 minutos (${expiresInSeconds}s)`);
            }
          }
        }
      } catch (e) {
        // Silenciosamente ignorar erros de parse - não é JSON
      }
    }
  },

  // Novo método para logar informações sobre o ambiente de autenticação
  logAuthEnvironment: (): void => {
    const sessionLogger = createLogger('SUPABASE', 'SESSION');
    const envInfo = {
      platform: Platform?.OS || 'unknown',
      isServer: typeof window === 'undefined' && Platform?.OS === 'web',
      deviceInfo: {
        // Evitando erros de tipagem com as propriedades do Platform.constants
        brand: typeof Platform?.constants === 'object' ?
          (Platform.constants as Record<string, any>)?.['Brand'] || 'N/A' : 'N/A',
        model: typeof Platform?.constants === 'object' ?
          (Platform.constants as Record<string, any>)?.['Model'] || 'N/A' : 'N/A',
      },
      reactNativeVersion: Platform?.Version || 'N/A',
    };

    sessionLogger.info('Ambiente de autenticação:', envInfo);
  }
};

// Loggers específicos para cada componente principal do app
export const InitializeLogger = {
  App: createLogger('INITIALIZE', 'APP'),
  Main: createLogger('INITIALIZE'),
};

// Adicionando logger específico para serviço de rede
export const NetworkLogger = {
  Main: createLogger('NETWORK'),
  Status: createLogger('NETWORK', 'STATUS'),
  Events: createLogger('NETWORK', 'EVENTS'),
};

export const StateLogger = {
  Global: createLogger('STATE', 'GLOBAL'),
  User: createLogger('STATE', 'USER'),
  Main: createLogger('STATE'),

  // Submódulos para sincronização
  SyncMain: createLogger('STATE', 'SYNC-MAIN'),
  SyncPrerequisites: createLogger('STATE', 'SYNC-PREREQUISITES'),
  SyncPush: createLogger('STATE', 'SYNC-PUSH'),
  SyncPull: createLogger('STATE', 'SYNC-PULL'),
  SyncStatus: createLogger('STATE', 'SYNC-STATUS'),
  SyncError: createLogger('STATE', 'SYNC-ERROR'),
  SyncRetry: createLogger('STATE', 'SYNC-RETRY'),

  // Submódulos para realtime
  RealtimeMain: createLogger('STATE', 'REALTIME-MAIN'),
  RealtimeSetup: createLogger('STATE', 'REALTIME-SETUP'),
  RealtimeEvents: createLogger('STATE', 'REALTIME-EVENTS'),
  RealtimeCleanup: createLogger('STATE', 'REALTIME-CLEANUP'),
  RealtimeError: createLogger('STATE', 'REALTIME-ERROR'),

  // Submódulos para estado computado
  ComputedState: createLogger('STATE', 'COMPUTED-STATE'),

  // submódulos para appAction 
  AppActionSetReady: createLogger('STATE', 'APP-ACTION-SET-READY'),
};

// Loggers específicos para o serviço de sincronização
export const SyncLogger = {
  Initialize: createLogger('SYNC', 'INITIALIZE'),
  Shutdown: createLogger('SYNC', 'SHUTDOWN'),
  Prerequisites: createLogger('SYNC', 'PREREQUISITES'),
  Monitor: createLogger('SYNC', 'MONITOR'),
  Push: createLogger('SYNC', 'PUSH'),
  Pull: createLogger('SYNC', 'PULL'),
  Retry: createLogger('SYNC', 'RETRY'),
  Error: createLogger('SYNC', 'ERROR'),
  Status: createLogger('SYNC', 'STATUS'),
  Conflict: createLogger('SYNC', 'CONFLICT'),
  Progress: createLogger('SYNC', 'PROGRESS'),
  Main: createLogger('SYNC', 'MAIN'),
  Timeout: createLogger('SYNC', 'TIMEOUT'),
  OpCreate: createLogger('SYNC', 'OP-CREATE'),
  OpUpdate: createLogger('SYNC', 'OP-UPDATE'),
  OpDelete: createLogger('SYNC', 'OP-DELETE'),
  OpConfig: createLogger('SYNC', 'OP-CONFIG'),
  OpChecklist: createLogger('SYNC', 'OP-CHECKLIST'),
};

// Loggers específicos para o serviço de realtime
export const RealtimeLogger = {
  Main: createLogger('REALTIME'),
  Setup: createLogger('REALTIME', 'SETUP'),
  Cleanup: createLogger('REALTIME', 'CLEANUP'),
  Events: createLogger('REALTIME', 'EVENTS'),
  Error: createLogger('REALTIME', 'ERROR'),
  Reconnect: createLogger('REALTIME', 'RECONNECT'),
};

// Exportar uma função para criar loggers personalizados
export const getLogger = (module: string, subModule?: string): ILogger => {
  return createLogger(module, subModule);
};

// Loggers específicos para arquivos da aplicação
export const AppLayoutLogger = {
  Main: createLogger('APP-LAYOUT'),
  Init: createLogger('APP-LAYOUT', 'INIT'),
  Render: createLogger('APP-LAYOUT', 'RENDER')
};

export const ToastLogger = {
  Main: createLogger('TOAST')
}; 

export const IdChecklistLogger = {
  Main: createLogger('ID-CHECKLIST')
};

export const UseChecklistEditorLogger = {
  Main: createLogger('USE-CHECKLIST-EDITOR')
};

export const PDFServiceWebLogger = {
  Main: createLogger('PDF-SERVICE-WEB')
};

export const PDFDocBuilderLogger = {
  Main: createLogger('PDF-DOC-BUILDER')
};

export const ChecklistActionsHelperLogger = {
  Main: createLogger('CHECKLIST-ACTIONS-HELPER')
};

export const AdminLogger = {
  Main: createLogger('ADMIN'),
  Dashboard: createLogger('ADMIN', 'DASHBOARD'),
  Checklists: createLogger('ADMIN', 'CHECKLISTS'),
  Filter: createLogger('ADMIN', 'FILTER'),
  Table: createLogger('ADMIN', 'TABLE'),
  List: createLogger('ADMIN', 'LIST'),
  KPI: createLogger('ADMIN', 'KPI'),
};

export const ChecklistTemplatesLogger = {
  Main: createLogger('APP-(APP)-SETTINGS-CHECKLISTS-TEMPLATES'),
};

export const ChecklistFieldsFormLogger = {
  Main: createLogger('CHECKLIST-FIELDS-FORM')
};

export const ChecklistMarkFieldLogger = {
  Main: createLogger('CHECKLIST-MARK-FIELD')
};

export const ChecklistPhotoFieldLogger = {
  Main: createLogger('CHECKLIST-PHOTO-FIELD')
};

export const PendingImagePickerRecoveryLogger = {
  Main: createLogger('PENDING-IMAGE-PICKER-RECOVERY')
};

export const ChecklistDropdownFieldLogger = {
  Main: createLogger('CHECKLIST-DROPDOWN-FIELD')
};

export const ChecklistCEPFieldLogger = {
  Main: createLogger('CHECKLIST-CEP-FIELD')
};

export const ChecklistImageServiceLogger = {
  Main: createLogger('CHECKLIST-IMAGE-SERVICE')
};

export const ChecklistImageCleanupHelperLogger = {
  Main: createLogger('CHECKLIST-IMAGE-CLEANUP-HELPER')
};

export const ChecklistTemplateCleanupHelperLogger = {
  Main: createLogger('CHECKLIST-TEMPLATE-CLEANUP-HELPER')
};

export const ChecklistFieldCleanupHelperLogger = {
  Main: createLogger('CHECKLIST-FIELD-CLEANUP-HELPER'),
  Marcacao: createLogger('CHECKLIST-FIELD-CLEANUP-HELPER', 'MARCACAO'),
  Pending: createLogger('CHECKLIST-FIELD-CLEANUP-HELPER', 'PENDING')
};

export const AppIndexLogger = {
  Main: createLogger('APP-INDEX'),
  Init: createLogger('APP-INDEX', 'INIT'),
  Render: createLogger('APP-INDEX', 'RENDER'),
  UserState: createLogger('APP-INDEX', 'USER_STATE')
};

export const AppAppIndexLogger = {
  Main: createLogger('APP-(APP)-INDEX'),
  Init: createLogger('APP-(APP)-INDEX', 'INIT'),
  Render: createLogger('APP-(APP)-INDEX', 'RENDER'),
  UserState: createLogger('APP-(APP)-INDEX', 'USER_STATE'),
  Subscribe: createLogger('APP-(APP)-INDEX', 'SUBSCRIBE'),
  Checklist: createLogger('APP-(APP)-INDEX', 'CHECKLIST')
};

// Loggers para serviços de links de autenticação
export const AuthLinksLogger = {
  Main: createLogger('AUTH-LINKS'),
  GetURL: createLogger('AUTH-LINKS', 'GET_URL'),
  SetupDeepLinks: createLogger('AUTH-LINKS', 'SETUP_DEEP_LINKS'),
  OpenBrowser: createLogger('AUTH-LINKS', 'OPEN_BROWSER'),
};

// Loggers para serviços de checklist
export const ChecklistServiceLogger = {
  Main: createLogger('CHECKLIST-SERVICE'),
};

export const ChecklistMarcacaoImageHelperLogger = {
  Main: createLogger('MARCACAO-IMAGE-HELPER')
};

// Loggers para telas de autenticação
export const AppAuthLoginLogger = {
  Login: createLogger('APP-(AUTH)-LOGIN', 'LOGIN'),
  Register: createLogger('APP-(AUTH)-LOGIN', 'REGISTER'),
  UserCheck: createLogger('APP-(AUTH)-LOGIN', 'USER_CHECK'),
  Main: createLogger('APP-(AUTH)-LOGIN'),
};

// Loggers para telas de autenticação
export const AppAuthSignupLogger = {
  Signup: createLogger('APP-(AUTH)-SIGNUP', 'SIGNUP'),
  UserCheck: createLogger('APP-(AUTH)-SIGNUP', 'USER_CHECK'),
  Main: createLogger('APP-(AUTH)-SIGNUP'),
};

export const AuthServicesLogger = {
  Login: createLogger('AUTH-SERVICES', 'LOGIN'),
  Register: createLogger('AUTH-SERVICES', 'REGISTER'),
  UserCheck: createLogger('AUTH-SERVICES', 'USER_CHECK'),
  Main: createLogger('AUTH-SERVICES'),
  PasswordReset: createLogger('AUTH-SERVICES', 'PASSWORD_RESET'),
};

// Loggers específicos para banco de dados
export const DatabaseLogger = {
  Main: createLogger('DATABASE'),
  Adapter: createLogger('DATABASE', 'ADAPTER'),
  Service: createLogger('DATABASE', 'SERVICE'),
  SQLite: createLogger('DATABASE', 'SQLITE'),
  Dexie: createLogger('DATABASE', 'DEXIE'),
  Query: createLogger('DATABASE', 'QUERY'),
  Transaction: createLogger('DATABASE', 'TRANSACTION'),
  Sync: createLogger('DATABASE', 'SYNC'),
};

// Logger específico para SQLiteAdapter com submodules por método
export const SQLiteAdapterLogger = {
  Main: createLogger('SQLITE-ADAPTER'),
  Initialize: createLogger('SQLITE-ADAPTER', 'INITIALIZE'),
  CreateTables: createLogger('SQLITE-ADAPTER', 'CREATE_TABLES'),
  ExecuteQuery: createLogger('SQLITE-ADAPTER', 'EXECUTE_QUERY'),
  BulkUpsert: createLogger('SQLITE-ADAPTER', 'BULK_UPSERT'),
  CreateItem: createLogger('SQLITE-ADAPTER', 'CREATE_ITEM'),
  ReadItem: createLogger('SQLITE-ADAPTER', 'READ_ITEM'),
  UpdateItem: createLogger('SQLITE-ADAPTER', 'UPDATE_ITEM'),
  DeleteItem: createLogger('SQLITE-ADAPTER', 'DELETE_ITEM'),
  ListItems: createLogger('SQLITE-ADAPTER', 'LIST_ITEMS'),
  RegisterPendingOp: createLogger('SQLITE-ADAPTER', 'REGISTER_PENDING_OP'),
  GetPendingOps: createLogger('SQLITE-ADAPTER', 'GET_PENDING_OPS'),
  RemovePendingOp: createLogger('SQLITE-ADAPTER', 'REMOVE_PENDING_OP'),
  Close: createLogger('SQLITE-ADAPTER', 'CLOSE'),
};

// Logger específico para DatabaseService com submodules por método
export const DatabaseServiceLogger = {
  Main: createLogger('DATABASE-SERVICE'),
  Initialize: createLogger('DATABASE-SERVICE', 'INITIALIZE'),
  BulkUpsert: createLogger('DATABASE-SERVICE', 'BULK_UPSERT'),
  CreateItem: createLogger('DATABASE-SERVICE', 'CREATE_ITEM'),
  ReadItem: createLogger('DATABASE-SERVICE', 'READ_ITEM'),
  UpdateItem: createLogger('DATABASE-SERVICE', 'UPDATE_ITEM'),
  DeleteItem: createLogger('DATABASE-SERVICE', 'DELETE_ITEM'),
  ListItems: createLogger('DATABASE-SERVICE', 'LIST_ITEMS'),
  GetPendingOps: createLogger('DATABASE-SERVICE', 'GET_PENDING_OPS'),
  RemovePendingOp: createLogger('DATABASE-SERVICE', 'REMOVE_PENDING_OP'),
  Close: createLogger('DATABASE-SERVICE', 'CLOSE'),
  ValidateInit: createLogger('DATABASE-SERVICE', 'VALIDATE_INIT'),
  BulkUpsertChecklistValues: createLogger('DATABASE-SERVICE', 'BULK_UPSERT_CHECKLIST_VALUES'),
};

// Logger específico para DexieAdapter com submodules por método
export const DexieAdapterLogger = {
  Main: createLogger('DEXIE-ADAPTER'),
  Initialize: createLogger('DEXIE-ADAPTER', 'INITIALIZE'),
  CreateTables: createLogger('DEXIE-ADAPTER', 'CREATE_TABLES'),
  BulkUpsert: createLogger('DEXIE-ADAPTER', 'BULK_UPSERT'),
  CreateItem: createLogger('DEXIE-ADAPTER', 'CREATE_ITEM'),
  ReadItem: createLogger('DEXIE-ADAPTER', 'READ_ITEM'),
  UpdateItem: createLogger('DEXIE-ADAPTER', 'UPDATE_ITEM'),
  DeleteItem: createLogger('DEXIE-ADAPTER', 'DELETE_ITEM'),
  ListItems: createLogger('DEXIE-ADAPTER', 'LIST_ITEMS'),
  RegisterPendingOp: createLogger('DEXIE-ADAPTER', 'REGISTER_PENDING_OP'),
  GetPendingOps: createLogger('DEXIE-ADAPTER', 'GET_PENDING_OPS'),
  RemovePendingOp: createLogger('DEXIE-ADAPTER', 'REMOVE_PENDING_OP'),
  Close: createLogger('DEXIE-ADAPTER', 'CLOSE'),
};

// Loggers específicos para serviço de assinatura
export const SubscriptionServiceLogger = {
  Main: createLogger('SUBSCRIPTION-SERVICE'),
  Status: createLogger('SUBSCRIPTION-SERVICE', 'STATUS'),
  Plans: createLogger('SUBSCRIPTION-SERVICE', 'PLANS'),
  Payments: createLogger('SUBSCRIPTION-SERVICE', 'PAYMENTS'),
  Subscribe: createLogger('SUBSCRIPTION-SERVICE', 'SUBSCRIBE'),
  LocalDb: createLogger('SUBSCRIPTION-SERVICE', 'LOCAL_DB'),
  RemoteDb: createLogger('SUBSCRIPTION-SERVICE', 'REMOTE_DB'),
};

// Loggers específicos para telas do app
export const HomeLogger = {
  Main: createLogger('HOME'),
  Init: createLogger('HOME', 'INIT'),
  Render: createLogger('HOME', 'RENDER'),
  Subscribe: createLogger('HOME', 'SUBSCRIBE'),
};

export const ProfileLogger = {
  Main: createLogger('PROFILE'),
  Info: createLogger('PROFILE', 'INFO'),
  Edit: createLogger('PROFILE', 'EDIT'),
  Plans: createLogger('PROFILE', 'PLANS'),
  Payments: createLogger('PROFILE', 'PAYMENTS'),
};

export const PaymentsLogger = {
  Main: createLogger('PAYMENTS'),
  Info: createLogger('PAYMENTS', 'INFO'),
  Edit: createLogger('PAYMENTS', 'EDIT'),
  Plans: createLogger('PAYMENTS', 'PLANS'),
  Payments: createLogger('PAYMENTS', 'PAYMENTS'),
};

export const PlansLogger = {
  Main: createLogger('PLANS'),
  Info: createLogger('PLANS', 'INFO'),
  Edit: createLogger('PLANS', 'EDIT'),
  Plans: createLogger('PLANS', 'PLANS'),
  Payments: createLogger('PLANS', 'PAYMENTS'),
};

export const TabNavigatorLogger = {
  Main: createLogger('TAB-NAVIGATOR'),
  Init: createLogger('TAB-NAVIGATOR', 'INIT'),
  Navigation: createLogger('TAB-NAVIGATOR', 'NAVIGATION'),
};

export const UserServiceLogger = {
  Main: createLogger('USER-SERVICE'),
  Profile: createLogger('USER-SERVICE', 'PROFILE'),
  Update: createLogger('USER-SERVICE', 'UPDATE'),
  GetUserProfile: createLogger('USER-SERVICE', 'GET_USER_PROFILE'),
  GetUserCompany: createLogger('USER-SERVICE', 'GET_USER_COMPANY'),
  UpdateUserProfile: createLogger('USER-SERVICE', 'UPDATE_USER_PROFILE'),
  UpdateCompanyProfile: createLogger('USER-SERVICE', 'UPDATE_COMPANY_PROFILE'),
  UploadUserProfileImage: createLogger('USER-SERVICE', 'UPLOAD_USER_PROFILE_IMAGE'),
};

export const UploadImageHelperLogger = {
  Main: createLogger('UPLOAD-IMAGE-HELPER'),
  Upload: createLogger('UPLOAD-IMAGE-HELPER', 'UPLOAD'),
  Delete: createLogger('UPLOAD-IMAGE-HELPER', 'DELETE'),
  Utils: createLogger('UPLOAD-IMAGE-HELPER', 'UTILS'),
};

export const FileHelperLogger = {
  Main: createLogger('FILE-HELPER'),
  FetchFile: createLogger('FILE-HELPER', 'FETCH_FILE'),
  DeleteLocalFile: createLogger('FILE-HELPER', 'DELETE_LOCAL_FILE'),
};

export const SettingsLayoutLogger = {
  Main: createLogger('APP-(APP)-SETTINGS-LAYOUT'),
};

export const SettingsIndexLogger = {
  Main: createLogger('APP-(APP)-SETTINGS-INDEX'),
};

export const SettingsSupportLogger = {
  Main: createLogger('APP-(APP)-SETTINGS-SUPPORT'),
};

export const SettingsSubscriptionLogger = {
  Main: createLogger('APP-(APP)-SETTINGS-SUBSCRIPTION'),
  Plans: createLogger('APP-(APP)-SETTINGS-SUBSCRIPTION', 'PLANS'),
  Payments: createLogger('APP-(APP)-SETTINGS-SUBSCRIPTION', 'PAYMENTS'),
  Details: createLogger('APP-(APP)-SETTINGS-SUBSCRIPTION', 'DETAILS')
};

export const SettingsPaymentsLogger = {
  Main: createLogger('APP-(APP)-SETTINGS-PAYMENTS'),
};

export const SettingsCompanyLogger = {
  Main: createLogger('APP-(APP)-SETTINGS-COMPANY'),
};

export const SettingsAdvancedLogger = {
  Main: createLogger('APP-(APP)-SETTINGS-ADVANCED'),
};

export const SettingsAccountLogger = {
  Main: createLogger('APP-(APP)-SETTINGS-ACCOUNT'),
};

export const SettingsAddUsersLogger = {
  Main: createLogger('APP-(APP)-SETTINGS-ADD-USERS'),
};

export const DevPanelLogger = {
  Main: createLogger('APP-(APP)-PAINEL-DEV'),
  Data: createLogger('APP-(APP)-PAINEL-DEV', 'DATA'),
};

export const DevChecklistExplorerLogger = {
  Main: createLogger('APP-(APP)-PAINEL-DEV-EXPLORAR'),
  Data: createLogger('APP-(APP)-PAINEL-DEV-EXPLORAR', 'DATA'),
};

export const DevChecklistEditorLogger = {
  Main: createLogger('APP-(APP)-PAINEL-DEV-CHECKLIST'),
  Data: createLogger('APP-(APP)-PAINEL-DEV-CHECKLIST', 'DATA'),
  Save: createLogger('APP-(APP)-PAINEL-DEV-CHECKLIST', 'SAVE'),
  Image: createLogger('APP-(APP)-PAINEL-DEV-CHECKLIST', 'IMAGE'),
};

export const FieldOrderingLogger = {
  Main: createLogger('FIELD-ORDERING')
};

export const SettingsChecklistsLogger = {
  Main: createLogger('APP-(APP)-SETTINGS-CHECKLISTS'),
  Ordering: createLogger('APP-(APP)-SETTINGS-CHECKLISTS', 'ORDERING'),
  Fields: createLogger('APP-(APP)-SETTINGS-CHECKLISTS', 'FIELDS'),
};

export const CampoMarcacaoBackgroundButtonLogger = {
  Main: createLogger('CAMPO-MARCACAO-BACKGROUND-BUTTON')
};

export default {
  createLogger,
  SupabaseLogger,
  InitializeLogger,
  StateLogger,
  AppLayoutLogger,
  AppIndexLogger,
  DatabaseLogger,
  SQLiteAdapterLogger,
  DatabaseServiceLogger,
  DexieAdapterLogger,
  SyncLogger,
  RealtimeLogger,
  getLogger,
  TokenUtils,
  AppAuthLoginLogger,
  SubscriptionServiceLogger,
  HomeLogger,
  ProfileLogger,
  TabNavigatorLogger,
  UserServiceLogger,
}; 
